import * as React from 'react'
import { Link } from 'react-router-dom'
import { Phone, Mail, MapPin } from 'lucide-react'
import { useLanguage } from '../../contexts/LanguageContext'
import { useTheme } from '../../contexts/ThemeContext'

const Footer: React.FC = () => {
  const { t, isRTL } = useLanguage()
  const { theme } = useTheme()

  return (
    <footer className={`bg-black text-white ${isRTL ? 'font-arabic' : 'font-english'}`}>
      {/* Main Footer Content */}
      <div className="container-fmc py-16">
        <div className={`grid-fmc md:grid-cols-2 lg:grid-cols-5 ${isRTL ? 'text-right' : 'text-left'}`}>
          {/* Company Info */}
          <div className="space-y-6">
            <div className={`flex items-center ${isRTL ? 'justify-end' : 'justify-start'}`}>
              <div className="w-24 h-28 flex items-center justify-center">
                <img 
                  src="/logos/logo.png" 
                  alt="FMC Logo" 
                  className="w-full h-full object-contain"
                />
              </div>
            </div>
            <p className={`${theme === 'dark' ? 'text-gray-400' : 'text-secondary-300'} leading-relaxed`}>
              {t('footer.company.description')}
            </p>
            <div className={`flex ${isRTL ? 'justify-end' : 'justify-start'} space-x-4 ${isRTL ? 'space-x-reverse' : ''}`}>
              <a 
                href="https://www.linkedin.com/company/fmc-construction-co/" 
                target="_blank"
                rel="noopener noreferrer"
                className={`w-10 h-10 bg-blue-600 hover:bg-blue-700 rounded-lg flex items-center justify-center transition-colors duration-200`}
                aria-label="LinkedIn"
              >
                <svg 
                  className="w-5 h-5 text-white" 
                  viewBox="0 0 24 24" 
                  fill="currentColor"
                >
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                </svg>
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div className="space-y-6">
            <h4 className="text-xl font-bold text-white">{t('footer.quickLinks.title')}</h4>
            <ul className="space-y-3">
              <li>
                <Link to="/about" className={`${theme === 'dark' ? 'text-gray-400' : 'text-secondary-300'} hover:text-primary-400 transition-colors duration-200`}>
                  {t('nav.about')}
                </Link>
              </li>
              <li>
                <Link to="/industries-sectors" className={`${theme === 'dark' ? 'text-gray-400' : 'text-secondary-300'} hover:text-primary-400 transition-colors duration-200`}>
                  {t('nav.sectorsIndustries')}
                </Link>
              </li>
              <li>
                <Link to="/our-services" className={`${theme === 'dark' ? 'text-gray-400' : 'text-secondary-300'} hover:text-primary-400 transition-colors duration-200`}>
                  {t('nav.services')}
                </Link>
              </li>
              <li>
                <Link to="/projects" className={`${theme === 'dark' ? 'text-gray-400' : 'text-secondary-300'} hover:text-primary-400 transition-colors duration-200`}>
                  {t('nav.projects')}
                </Link>
              </li>
              <li>
                <Link to="/contact" className={`${theme === 'dark' ? 'text-gray-400' : 'text-secondary-300'} hover:text-primary-400 transition-colors duration-200`}>
                  {t('nav.contact')}
                </Link>
              </li>
              <li>
                <Link to="/blog" className={`${theme === 'dark' ? 'text-gray-400' : 'text-secondary-300'} hover:text-primary-400 transition-colors duration-200`}>
                  {t('footer.links.blog')}
                </Link>
              </li>
            </ul>
          </div>

          {/* Services */}
          <div className="space-y-6">
            <h4 className="text-xl font-bold text-white">{t('footer.services.title')}</h4>
            <ul className="space-y-3">
              <li>
                <Link to="/services/building-infrastructure" className={`${theme === 'dark' ? 'text-gray-400' : 'text-secondary-300'} hover:text-primary-400 transition-colors duration-200`}>
                  {t('services.buildingInfrastructure')}
                </Link>
              </li>
              <li>
                <Link to="/services/design-engineering" className={`${theme === 'dark' ? 'text-gray-400' : 'text-secondary-300'} hover:text-primary-400 transition-colors duration-200`}>
                  {t('services.designEngineering')}
                </Link>
              </li>
              <li>
                <Link to="/services/architectural-fitout" className={`${theme === 'dark' ? 'text-gray-400' : 'text-secondary-300'} hover:text-primary-400 transition-colors duration-200`}>
                  {t('services.architecturalFitout')}
                </Link>
              </li>
              <li>
                <Link to="/services/civil-works" className={`${theme === 'dark' ? 'text-gray-400' : 'text-secondary-300'} hover:text-primary-400 transition-colors duration-200`}>
                  {t('services.civilWorks')}
                </Link>
              </li>
              <li>
                <Link to="/services/mep-works" className={`${theme === 'dark' ? 'text-gray-400' : 'text-secondary-300'} hover:text-primary-400 transition-colors duration-200`}>
                  {t('services.mepWorks')}
                </Link>
              </li>
              <li>
                <Link to="/services/data-centers" className={`${theme === 'dark' ? 'text-gray-400' : 'text-secondary-300'} hover:text-primary-400 transition-colors duration-200`}>
                  {t('services.dataCenters')}
                </Link>
              </li>
              <li>
                <Link to="/services/pricing" className={`${theme === 'dark' ? 'text-gray-400' : 'text-secondary-300'} hover:text-primary-400 transition-colors duration-200`}>
                  {t('services.pricingBidding')}
                </Link>
              </li>
              <li>
                <Link to="/services/maintenance" className={`${theme === 'dark' ? 'text-gray-400' : 'text-secondary-300'} hover:text-primary-400 transition-colors duration-200`}>
                  {t('services.maintenanceWorks')}
                </Link>
              </li>
            </ul>
          </div>

          {/* Sectors */}
          <div className="space-y-6">
            <h4 className="text-xl font-bold text-white">{t('sectors.title')}</h4>
            <ul className="space-y-3">
              <li>
                <Link to="/sectors/commercial" className={`${theme === 'dark' ? 'text-gray-400' : 'text-secondary-300'} hover:text-primary-400 transition-colors duration-200`}>
                  {t('sectors.commercial')}
                </Link>
              </li>
              <li>
                <Link to="/sectors/entertainment" className={`${theme === 'dark' ? 'text-gray-400' : 'text-secondary-300'} hover:text-primary-400 transition-colors duration-200`}>
                  {t('sectors.entertainment')}
                </Link>
              </li>
              <li>
                <Link to="/sectors/hotel" className={`${theme === 'dark' ? 'text-gray-400' : 'text-secondary-300'} hover:text-primary-400 transition-colors duration-200`}>
                  {t('sectors.hotel')}
                </Link>
              </li>
              <li>
                <Link to="/sectors/industrial" className={`${theme === 'dark' ? 'text-gray-400' : 'text-secondary-300'} hover:text-primary-400 transition-colors duration-200`}>
                  {t('sectors.industrial')}
                </Link>
              </li>
              <li>
                <Link to="/sectors/residential" className={`${theme === 'dark' ? 'text-gray-400' : 'text-secondary-300'} hover:text-primary-400 transition-colors duration-200`}>
                  {t('sectors.residential')}
                </Link>
              </li>
              <li>
                <Link to="/sectors/school" className={`${theme === 'dark' ? 'text-gray-400' : 'text-secondary-300'} hover:text-primary-400 transition-colors duration-200`}>
                  {t('sectors.school')}
                </Link>
              </li>
              <li>
                <Link to="/sectors/utilities" className={`${theme === 'dark' ? 'text-gray-400' : 'text-secondary-300'} hover:text-primary-400 transition-colors duration-200`}>
                  {t('sectors.utilities')}
                </Link>
              </li>
              <li>
                <Link to="/sectors/warehouse" className={`${theme === 'dark' ? 'text-gray-400' : 'text-secondary-300'} hover:text-primary-400 transition-colors duration-200`}>
                  {t('sectors.warehouse')}
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact Info */}
          <div className="space-y-6">
            <h4 className="text-xl font-bold text-white">{t('footer.contact.title')}</h4>
            <div className="space-y-4">
              <div className={`flex items-start space-x-3 ${isRTL ? 'flex-row-reverse space-x-reverse' : ''}`}>
                <MapPin className="w-5 h-5 text-primary-500 mt-0.5" />
                <div>
                  <p className={`${theme === 'dark' ? 'text-gray-400' : 'text-secondary-300'}`}>{t('contact.companyName' as any)}</p>
                  <p className={`${theme === 'dark' ? 'text-gray-400' : 'text-secondary-300'}`}>{t('contact.address.poBox' as any)}</p>
                  <p className={`${theme === 'dark' ? 'text-gray-400' : 'text-secondary-300'}`}>{t('contact.address.postalCode' as any)}</p>
                  <p className={`${theme === 'dark' ? 'text-gray-400' : 'text-secondary-300'}`}>{t('contact.address.country' as any)}</p>
                </div>
              </div>
              
              <div className="flex flex-col space-y-2">
                <div className={`flex items-center space-x-3 ${isRTL ? 'flex-row-reverse space-x-reverse' : ''}`}>
                  <Phone className="w-5 h-5 text-primary-500 flex-shrink-0" />
                  <span className={`${theme === 'dark' ? 'text-gray-400' : 'text-secondary-300'}`}>+966 12 238 8089</span>
                </div>
                <div className={`flex items-center space-x-3 ${isRTL ? 'flex-row-reverse space-x-reverse' : ''}`}>
                  <Phone className="w-5 h-5 text-primary-500 flex-shrink-0 opacity-0" aria-hidden="true" />
                  <span className={`${theme === 'dark' ? 'text-gray-400' : 'text-secondary-300'}`}>+966 12 221 2598</span>
                </div>
              </div>
              
              <div className={`flex items-center space-x-3 ${isRTL ? 'flex-row-reverse space-x-reverse' : ''}`}>
                <Mail className="w-5 h-5 text-primary-500" />
                <span className={`${theme === 'dark' ? 'text-gray-400' : 'text-secondary-300'}`}>info@fmcksa.com</span>
              </div>
              
              {/* Vision 2030 Logo */}
              <div className={`mt-4 flex ${isRTL ? 'justify-end' : 'justify-start'}`}>
                <img 
                  src="/images/Saudi_Vision_2030_logo.png" 
                  alt="Saudi Vision 2030" 
                  className={`h-[75px] w-auto object-contain ${isRTL ? 'mr-8' : 'ml-0'}`}
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.src = "/images/Saudi_Vision_2030_logo.webp";
                  }}
                />
              </div>
              
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Footer */}
      <div className={`border-t ${theme === 'dark' ? 'border-gray-800' : 'border-secondary-800'}`}>
        <div className="container-fmc py-6">
          <div className={`flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0 ${isRTL ? 'md:flex-row-reverse' : ''}`}>
            <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-secondary-400'} ${isRTL ? 'text-right' : 'text-left'}`}>
              {t('footer.copyright')}
            </p>
            
            <div className={`flex items-center gap-6 ${isRTL ? 'flex-row-reverse' : ''}`}>
              <Link to="/site-map" className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-secondary-400'} hover:text-primary-400 transition-colors`}>
                {t('site.map')}
              </Link>
              <Link to="/cookie-policy" className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-secondary-400'} hover:text-primary-400 transition-colors`}>
                {t('footer.legal.cookie')}
              </Link>
              <Link to="/terms-of-service" className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-secondary-400'} hover:text-primary-400 transition-colors`}>
                {t('footer.legal.terms')}
              </Link>
              <Link to="/privacy-policy" className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-secondary-400'} hover:text-primary-400 transition-colors`}>
                {t('footer.legal.privacy')}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  )
}

export default Footer